/**
 * Flow tab content for the workflow detail page.
 *
 * This module provides the FlowTab component and the handleValidateFlow
 * utility that integrates with the workflow-detail.tsx page.
 *
 * Integration into workflow-detail.tsx:
 * 1. Import FlowTab and add <TabsTrigger value="flow"> between schema and status
 * 2. Add <TabsContent value="flow"><FlowTab /></TabsContent>
 * 3. Call setFlowFromWorkflow(currentWorkflow) in the useEffect that fetches workflow data
 * 4. On tab change away from "flow", call flushPersist to save pending changes
 *
 * Tab order: settings | schema | flow | status (per D-02)
 */

import { useState, useCallback, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { Loader2 } from 'lucide-react'

import { useWorkflowStore } from '@/stores/workflow-store'
import { Button } from '@/components/ui/button'
import { FlowCanvas } from '@/components/workflows/flow/flow-canvas'
import { NodeConfigPanel } from '@/components/workflows/flow/node-config-panel'
import { EdgeConfigPanel } from '@/components/workflows/flow/edge-config-panel'
import { validateGraph } from '@/components/workflows/flow/validation'
import { flushPersist } from '@/stores/workflow-store-persistence'

/**
 * Flow tab content component.
 * Renders the canvas, config panels, and validate button.
 */
export function FlowTab() {
  const { t } = useTranslation('workflows')
  const flowNodes = useWorkflowStore((s) => s.flowNodes)
  const flowEdges = useWorkflowStore((s) => s.flowEdges)
  const [validating, setValidating] = useState(false)

  const handleValidateFlow = useCallback(() => {
    setValidating(true)

    // Run validation (synchronous but we show loading state for UX feedback)
    setTimeout(() => {
      const result = validateGraph(flowNodes, flowEdges)
      setValidating(false)

      if (result.valid) {
        toast.success(t('flow.validationSuccess'))
      } else {
        for (const error of result.errors) {
          toast.error(
            t('flow.validationError', { message: error.message })
          )
        }
      }
    }, 0)
  }, [flowNodes, flowEdges, t])

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div /> {/* Spacer */}
        <Button
          variant="outline"
          size="sm"
          onClick={handleValidateFlow}
          disabled={validating}
        >
          {validating ? (
            <Loader2 className="w-4 h-4 animate-spin mr-1" />
          ) : null}
          {t('flow.validateFlow')}
        </Button>
      </div>
      <FlowCanvas />
      <NodeConfigPanel />
      <EdgeConfigPanel />
    </div>
  )
}

/**
 * Hook to flush graph persistence on tab switch away from Flow.
 * Call this in the workflow-detail page's tab change handler.
 */
// eslint-disable-next-line react-refresh/only-export-components -- hook + component live in same module by design
export function useFlowTabPersistence() {
  const store = useWorkflowStore

  const handleTabChange = useCallback(
    (newTab: string, previousTab: string) => {
      if (previousTab === 'flow' && newTab !== 'flow') {
        flushPersist(store.getState as () => ReturnType<typeof store.getState>)
      }
    },
    [store]
  )

  // Also flush on page unload
  useEffect(() => {
    const handleBeforeUnload = () => {
      flushPersist(store.getState as () => ReturnType<typeof store.getState>)
    }
    window.addEventListener('beforeunload', handleBeforeUnload)
    return () => window.removeEventListener('beforeunload', handleBeforeUnload)
  }, [store])

  return { handleTabChange }
}

/**
 * Integration instructions for workflow-detail.tsx:
 *
 * ```tsx
 * import { FlowTab, useFlowTabPersistence } from './workflow-detail-flow-tab'
 *
 * function WorkflowDetailPage() {
 *   const { handleTabChange } = useFlowTabPersistence()
 *   const [activeTab, setActiveTab] = useState('settings')
 *
 *   const onTabChange = (value: string) => {
 *     handleTabChange(value, activeTab)
 *     setActiveTab(value)
 *   }
 *
 *   // In the Tabs component:
 *   <Tabs value={activeTab} onValueChange={onTabChange}>
 *     <TabsList>
 *       <TabsTrigger value="settings">{t('tabs.settings')}</TabsTrigger>
 *       <TabsTrigger value="schema">{t('tabs.schema')}</TabsTrigger>
 *       <TabsTrigger value="flow">{t('flow.tabLabel')}</TabsTrigger>
 *       <TabsTrigger value="status">{t('tabs.status')}</TabsTrigger>
 *     </TabsList>
 *
 *     <TabsContent value="flow">
 *       <FlowTab />
 *     </TabsContent>
 *   </Tabs>
 * ```
 *
 * Also add to the useEffect that fetches currentWorkflow:
 * ```tsx
 * useEffect(() => {
 *   if (currentWorkflow) {
 *     setFlowFromWorkflow(currentWorkflow)
 *   }
 * }, [currentWorkflow])
 * ```
 */
